import { readJsonString } from '@dealstack/shared/lib/format/json';
import type { DealEventAccessTct, DealEventTct } from '@dealstack/shared/lib/backendClient';
import { AuditAction } from '@dealstack/shared/lib/generated';
import {
	AUDITED_TABLE,
	DealEventCategory,
	DIRECTORY_PERSON_FIELD,
	DIRECTORY_RELATED_PERSON_FIELD,
	tablesInCategory,
} from '@dealstack/shared/lib/common/business/dealEvent';
import { formatNumberToUI } from '@dealstack/shared/lib/format/number';

import { normalizeScreamingSnakeToUI } from '@dealstack/shared/lib/format/string';
import { TransformedAuditData } from '@dealstack/frontend/shared/components/Audit/types.ts';
import {
	ALLOWED_FIELDS_BY_TABLE,
	formatFieldName,
	formatValue,
} from '@dealstack/frontend/shared/components/Audit/AuditLayout/utils.ts';
import { toAuditFieldValue } from '@dealstack/frontend/shared/components/Audit/AuditLayout/auditTableData.utils.ts';
import {
	formatCapitalCost,
	formatCapitalDate,
	formatCapitalUnits,
	formatHoldingEventType,
	formatRoles,
	humanizeIdentifier,
	isDeletionEvent,
} from '@dealstack/frontend/app/pages/Project/DealEventLog/dealEventLog.utils.ts';
import type { Translate } from '@dealstack/frontend/app/pages/Project/DealEventLog/DealEventLog.types.ts';

const NO_ROLES = '—';

const COMPOSED_ROW_LABEL: Readonly<Record<string, string>> = {
	person: 'deal_event_log.detail.person',
	employee: 'deal_event_log.detail.employee',
	employer: 'deal_event_log.detail.employer',
	customRelationType: 'deal_event_log.detail.custom_relation_type',
};

const getFieldLabel = (key: string, t: Translate, tableName: string): string => {
	const composed = COMPOSED_ROW_LABEL[key];

	if (composed) {
		return t(composed);
	}

	const mapped = formatFieldName(key, t, tableName);

	return mapped === key ? humanizeIdentifier(key) : mapped;
};

const formatChangeValue = (value: unknown, key: string, tableName: string): string =>
	typeof value === 'number' && Number.isInteger(value)
		? formatNumberToUI(value)
		: formatValue(
				toAuditFieldValue(value),
				key,
				undefined,
				undefined,
				undefined,
				undefined,
				tableName
			);

export const getDealEventChangeAction = (event: DealEventTct): AuditAction => {
	if (event.access) {
		return AuditAction.UPDATE;
	}

	if (event.oldData && event.newData) {
		return AuditAction.UPDATE;
	}

	return event.oldData ? AuditAction.DELETE : AuditAction.CREATE;
};

/**
 * An access row carries its change as roles rather than a recorded row diff. Every action reads
 * as a transition, with an empty side where a grant or a revoke has no roles.
 */
const getAccessChanges = (
	access: DealEventAccessTct,
	t: Translate,
	tRole: Translate
): TransformedAuditData[] => {
	const roles = (list: string[]) => formatRoles(list, tRole) || NO_ROLES;

	return [
		{
			id: 'roles',
			field: t('deal_event_log.detail.roles'),
			beforeValue: roles(access.rolesBefore),
			afterValue: roles(access.rolesAfter),
			isChanged: true,
			type: 'update',
		},
	];
};

export const getDealEventChanges = (
	event: DealEventTct,
	t: Translate,
	tRole: Translate
): TransformedAuditData[] => {
	if (event.access) {
		return getAccessChanges(event.access, t, tRole);
	}

	const tableName = event.entity.type;
	const action = getDealEventChangeAction(event);

	// Who the record belongs to sits with the change, not above it, and never reads as changed.
	const people = directoryPeople(event).map(({ key, name }): TransformedAuditData => {
		const field = getFieldLabel(key, t, tableName);

		return action === AuditAction.UPDATE
			? {
					id: key,
					field,
					beforeValue: name,
					afterValue: name,
					isChanged: false,
					type: 'update',
				}
			: { id: key, field, value: name, type: 'created' };
	});

	const changes = event.changedFields.map((key): TransformedAuditData => {
		const oldValue = event.oldData?.[key];
		const newValue = event.newData?.[key];
		const field = getFieldLabel(key, t, tableName);

		if (action === AuditAction.CREATE) {
			return {
				id: key,
				field,
				value: formatChangeValue(newValue, key, tableName),
				type: 'created',
			};
		}

		if (action === AuditAction.DELETE) {
			return {
				id: key,
				field,
				value: formatChangeValue(oldValue, key, tableName),
				type: 'deleted',
			};
		}

		return {
			id: key,
			field,
			beforeValue: formatChangeValue(oldValue, key, tableName),
			afterValue: formatChangeValue(newValue, key, tableName),
			isChanged:
				oldValue !== undefined &&
				newValue !== undefined &&
				JSON.stringify(oldValue) !== JSON.stringify(newValue),
			type: 'update',
		};
	});

	return [...people, ...changes];
};

type DetailValue = string | number | undefined;

/** A row of the panel's detail table: one recorded value, with nothing to diff it against. */
const detailRow = (
	key: string,
	value: DetailValue,
	t: Translate,
	tableName: string
): TransformedAuditData | undefined =>
	value === undefined || value === ''
		? undefined
		: {
				id: key,
				field: getFieldLabel(key, t, tableName),
				value: String(value),
				type: 'created',
			};

/** Shown as people rows instead, named by the feed rather than left as ids. */
const PERSON_FIELDS = new Set([
	'displayName',
	...Object.values(DIRECTORY_PERSON_FIELD),
	...Object.values(DIRECTORY_RELATED_PERSON_FIELD),
]);

/** Recorded on the row but not one the audit view shows. */
const EXTRA_DIRECTORY_FIELDS: Readonly<Record<string, readonly string[]>> = {
	[AUDITED_TABLE.PersonPerson]: ['customRelationType'],
};

const DIRECTORY_TABLES = new Set(tablesInCategory(DealEventCategory.ADMIN));

const getDirectoryFields = (tableName: string): string[] | undefined => {
	const audited = ALLOWED_FIELDS_BY_TABLE[tableName];

	if (!DIRECTORY_TABLES.has(tableName) || !audited) {
		return undefined;
	}

	return [
		...audited.filter((field) => !PERSON_FIELDS.has(field)),
		...(EXTRA_DIRECTORY_FIELDS[tableName] ?? []),
	];
};

/** Keyed so the audit view's own label applies where it has one for these people. */
const SUBJECT_PERSON_FIELD: Readonly<Record<string, string>> = {
	[AUDITED_TABLE.EmploymentRecord]: 'employee',
	[AUDITED_TABLE.PersonPerson]: 'sourcePersonId',
};

const RELATED_PERSON_FIELD: Readonly<Record<string, string>> = {
	[AUDITED_TABLE.EmploymentRecord]: 'employer',
	[AUDITED_TABLE.PersonPerson]: 'targetPersonId',
};

/** The people a directory record ties together, named by the feed rather than left as ids. */
const directoryPeople = (event: DealEventTct): { key: string; name: string }[] => {
	const { directory } = event;

	const tableName = event.entity.type;
	const isRename =
		tableName === AUDITED_TABLE.Person && event.changedFields.includes('displayName');

	if (!directory?.personName || isRename) {
		return [];
	}

	const related = RELATED_PERSON_FIELD[tableName];
	const people = [
		{ key: SUBJECT_PERSON_FIELD[tableName] ?? 'person', name: directory.personName },
	];

	if (related && directory.relatedPersonName) {
		people.push({ key: related, name: directory.relatedPersonName });
	}

	return people;
};

// PersonType is the one enum here the audit formatter has no case for.
const formatDirectoryValue = (
	value: unknown,
	key: string,
	tableName: string
): string | undefined => {
	if (value === undefined || value === null) {
		return undefined;
	}

	return key === 'type' && typeof value === 'string'
		? normalizeScreamingSnakeToUI(value)
		: formatChangeValue(value, key, tableName);
};

/**
 * What the event recorded, keyed by the field names the audit view already labels.
 *
 * Values come from the audit payload where there is one: the feed's capital block is fetched
 * live, so a create would otherwise report what a later edit left behind. A ledger row has no
 * payload and falls back to the block. Formatting is applied here rather than taken from the
 * diff's formatter, which leaves a Decimal payload ungrouped and would make the same field read
 * differently depending on which of the two sources it came from.
 */
const buildDetailRows = (
	event: DealEventTct,
	t: Translate
): (TransformedAuditData | undefined)[] => {
	const tableName = event.entity.type;
	const payload = event.newData ?? event.oldData;
	const { capital, holding } = event;

	const row = (key: string, value: DetailValue) => detailRow(key, value, t, tableName);

	const text = (key: string) => readJsonString(payload, key);

	const numeric = (key: string): number | undefined => {
		const value = payload?.[key];
		const parsed = typeof value === 'string' ? Number(value) : value;

		return typeof parsed === 'number' && Number.isFinite(parsed) ? parsed : undefined;
	};

	const asDate = (value: string | undefined) => (value ? formatCapitalDate(value) : undefined);

	const asUnits = (value: number | undefined) =>
		value === undefined ? undefined : formatCapitalUnits(value, capital?.unitDenominator);

	const asMoney = (value: number | undefined) =>
		value === undefined ? undefined : formatCapitalCost(value, capital?.currency);

	const asLabel = (value: string | undefined) =>
		value ? normalizeScreamingSnakeToUI(value) : undefined;

	const directoryFields = getDirectoryFields(tableName);

	if (directoryFields) {
		return [
			...directoryPeople(event).map(({ key, name }) => row(key, name)),
			...directoryFields.map((field) =>
				row(field, formatDirectoryValue(payload?.[field], field, tableName))
			),
		];
	}

	if (holding) {
		return [
			row('type', formatHoldingEventType(holding.eventType, t)),
			row('securityName', holding.securityName),
			row('date', asDate(text('date') ?? holding.date)),
			row('holderName', holding.holderName),
			row('pledgeToString', holding.pledgeToName),
			row('pledgeFromString', holding.pledgeAgainstName),
			row('assignedToString', holding.assignedToName),
			row('note', text('note') ?? holding.note),
		];
	}

	if (!capital) {
		return [];
	}

	if (tableName === AUDITED_TABLE.Security) {
		return [
			row('name', text('name') ?? capital.securityName),
			row('issuerId', capital.issuerName),
			row('currency', text('currency') ?? capital.currency),
		];
	}

	if (tableName === AUDITED_TABLE.ShareAssociation) {
		const expected = text('expectedTransactionType');

		return [
			row('securityName', capital.securityName),
			row('number', asUnits(numeric('number'))),
			row('cost', asMoney(numeric('cost'))),
			row('expectedTransactionType', asLabel(expected)),
		];
	}

	if (tableName === AUDITED_TABLE.Loan) {
		return [
			row('loanDefinitionName', capital.loanDefinitionName),
			row('amount', asMoney(numeric('amount'))),
			row('amountRemaining', asMoney(numeric('amountRemaining'))),
		];
	}

	if (tableName !== AUDITED_TABLE.Transaction) {
		return [];
	}

	const transactionType = text('type') ?? capital.transactionType;

	return [
		row('securityId', capital.securityName),
		row('type', asLabel(transactionType)),
		row('units', asUnits(numeric('units') ?? capital.units)),
		row('cost', asMoney(numeric('cost') ?? capital.cost)),
		row('date', asDate(text('date') ?? capital.date)),
		row('sourceId', capital.fromName),
		row('destinationId', capital.toName),
	];
};

/** A deletion's detail lists what was removed; anything else lists what was recorded. */
export const getDealEventDetailAction = (event: DealEventTct): AuditAction =>
	isDeletionEvent(event) ? AuditAction.DELETE : AuditAction.CREATE;

export const getDealEventDetails = (event: DealEventTct, t: Translate): TransformedAuditData[] => {
	const type = isDeletionEvent(event) ? 'deleted' : 'created';

	return buildDetailRows(event, t)
		.filter((row) => row !== undefined)
		.map((row) => ({ ...row, type }));
};

export const hasDealEventDetail = (event: DealEventTct): boolean =>
	event.changedFields.length > 0 ||
	!!event.access ||
	!!event.capital ||
	!!event.holding ||
	!!event.session ||
	!!event.directory;
